import { DisabledDate, DisabledPeriod } from "@finra/forms";

export const DEFAULT_TIMEZONE = 'America/New_York';

export function validatedDateRange(start: string, end: string) {
    const errors = [];
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    const today = new Date();
    const startDate = new Date(start);
    const endDate = new Date(end);
    const minDate = new Date('01/01/1900');

    if (start && startDate < minDate) {
        errors.push("Invalid From date. The date entered cannot be prior to 1/1/1900.");
    }

    if (start && startDate > today) {
        errors.push("From date cannot be future date.");
    }

    if (end && endDate > today) {
        errors.push("To date cannot be future date.");
    }
    if (start && end && endDate < startDate) {
        errors.push("The To date must be greater than or equal to the From date.");
    }

    if (!start || !dateRegex.test(start) || isNaN(startDate.valueOf())) {
        errors.push("From date is invalid.");
    }

    if (!end || !dateRegex.test(end) || isNaN(endDate.valueOf())) {
        errors.push("To date is invalid.");
    }

    if (!start && end) {
        errors.push("From date is required.");
    }

    if (start && !end) {
        errors.push("To date is required.");
    }

    return errors.length > 0 ? errors : true;
}

export function transform(value: string): string {
    if (!value) {
        console.log('transform value not set');
        return value;
    }

    //Remove non-digits
    let numValue = value.replace(/\D/g, '').substring(0, 8);

    if (numValue.length > 4) {
        //Format 01012 to 01/01/2 or 01012024 to 01/01/2024
        return numValue.replace(/(\d{2})(\d{2})(\d)/, '$1/$2/$3');
    } else if (numValue.length > 2) {
        //Format 010 to 01/0 or 0101 to 01/01
        return numValue.replace(/(\d{2})(\d)/, '$1/$2');
    } else {
        //No formatting
        return numValue;
    }
}

export function getDisabledDates(date: string, beforeOrAfter: 'before' | 'after'): DisabledDate[] {
    const retDates: DisabledDate[] = [];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date) {
        let inputDate = new Date(date.replace(/-/g, '/'));
        if (beforeOrAfter === 'after') {
            let nextDate = new Date(inputDate);
            nextDate.setDate(nextDate.getDate() + 1);
            const numberOfDays = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
            for (let index = nextDate.getDate(); index <= numberOfDays; index++) {
                retDates.push({
                    date: `${nextDate.getFullYear()}-${(nextDate.getMonth() + 1).toString().padStart(2, '0')}-${index.toString().padStart(2, '0')}`,
                    reason: "Start Date cannot be greater than End Date"
                });
            }
        } else {
            let prevDate = new Date(inputDate);
            prevDate.setDate(prevDate.getDate() - 1);
            for (let index = 1; index <= prevDate.getDate(); index++) {
                retDates.push({
                    date: `${prevDate.getFullYear()}-${(prevDate.getMonth() + 1).toString().padStart(2, '0')}-${index.toString().padStart(2, '0')}`,
                    reason: "End Date cannot be less than Start Date"
                });
            }
        }
    }

    // baseline return future dates
    const numberOfDays = new Date(tomorrow.getFullYear(), tomorrow.getMonth() + 1, 0).getDate();
    for (let index = tomorrow.getDate(); index <= numberOfDays; index++) {
        retDates.push({
            date: `${tomorrow.getFullYear()}-${(tomorrow.getMonth() + 1).toString().padStart(2, '0')}-${index.toString().padStart(2, '0')}`,
            reason: "This date cannot be a future date"
        });
    }

    return retDates;
}

export function getDisabledPeriods(date: string, beforeOrAfter: 'before' | 'after'): DisabledPeriod[] {
    const retPeriods: DisabledPeriod[] = []
    let nextMonth = new Date();
    nextMonth.setDate(1);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    //TODO: date picker component needs fixing, disabling start date before 2000 doesn't work without setting below ranges.
    retPeriods.push({
        start: '0100/1',
        end: '0999/1'
    });
    //From Date must be on or after 1/1/1900.
    retPeriods.push({
        start: '0999/1',
        end: '1900/1'
    });

    if (date) {
        const d = new Date(date.replace(/-/g, '/'));
        d.setDate(1);
        let start, end = '';
        if (beforeOrAfter === 'after') {
            d.setMonth(d.getMonth() + 1); // start the period at the next month chronologically
            start = `${d.getFullYear()}/${d.getMonth() + 1}`;
            end = '9999/1';
            retPeriods.push({ start, end });
        } else {
            start = '1899/12';
            end = `${d.getFullYear()}/${d.getMonth() + 1}`;
            retPeriods.push({ start, end });
        }
    }

    retPeriods.push({
        start: `${nextMonth.getFullYear()}/${nextMonth.getMonth() + 1}`,
        end: '9999/1'
    });

    return retPeriods;
}

/**
 * Parses a date string in either ISO format or MM/DD/YYYY format.
 *
 * @param {string} date - The date string to parse
 * @returns {Date|null} A Date object representing the parsed date, or null if the date is invalid or empty
 *
 * @example
 * // Returns a Date object for January 15, 2023
 * parseDate('01/15/2023');
 *
 * @example
 * // Returns a Date object for January 15, 2023
 * parseDate('2023-01-15T12:00:00.000Z');
 *
 * @example
 * // Returns null for invalid or empty input
 * parseDate('');
 */
export function parseDate(date: string) {
    if (!date) {
        return null;
    }

    let result: Date | null = null;

    // Handle custom format: YYYY-MM-DD-HH:MM (e.g., "2025-08-19-04:00")
    if (date.match(/^\d{4}-\d{2}-\d{2}-\d{2}:\d{2}$/)) {
        const parts = date.split('-');
        if (parts.length === 4) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            const day = parseInt(parts[2], 10);
            const timePart = parts[3]; // "04:00"
            const [hours, minutes] = timePart.split(':').map(num => parseInt(num, 10));

            result = new Date(year, month, day, hours, minutes);
        }
    }
    // Handle ISO format
    else if (date.includes('T') || (date.includes('-') && !date.match(/^\d{4}-\d{2}-\d{2}-\d{2}:\d{2}$/))) {
        result = new Date(date);
    }
    // Handle MM/DD/YYYY format
    else if (date.includes('/')) {
        const parts = date.split('/');
        if (parts.length === 3) {
            const month = parseInt(parts[0], 10) - 1;
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);

            result = new Date(year, month, day);
        }
    }

    // Return null if the date is invalid
    if (result && isNaN(result.getTime())) {
        return null;
    }

    return result;
}

/**
 * Calculates the difference in days between two dates.
 *
 * @param {Date|string} date1 - The first date
 * @param {Date|string} date2 - The second date
 * @param {Object} options - Configuration options
 * @param {boolean} [options.inclusive=false] - Whether to include both days in the calculation
 * @returns {number} The difference in days (positive if date2 is after date1, negative otherwise)
 *
 * @example
 * // Get the difference between two dates (exclusive)
 * getDaysDifference(new Date('2023-01-01'), new Date('2023-01-10')); // 9
 *
 * @example
 * // Get the inclusive difference between two dates
 * getDaysDifference(new Date('2023-01-01'), new Date('2023-01-10'), { inclusive: true }); // 10
 */
export function getDaysDifference(
    date1: Date | string,
    date2: Date | string,
    options: { inclusive?: boolean } = {}
): number {
    const { inclusive = false } = options;

    // Convert strings to Date objects if necessary, handling null/undefined
    if (!date1 || !date2) {
        return NaN;
    }

    const d1 = date1 instanceof Date ? date1 : parseDate(date1.toString());
    const d2 = date2 instanceof Date ? date2 : parseDate(date2.toString());

    // Check if both dates are valid
    if (!d1 || !d2 || isNaN(d1.getTime()) || isNaN(d2.getTime())) {
        return NaN;
    }

    // Reset time part to get full days - this ensures timezone-consistent comparisons
    const resetDate1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
    const resetDate2 = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());

    // Calculate the difference in milliseconds
    const differenceInMs = resetDate2.getTime() - resetDate1.getTime();

    // Convert milliseconds to days
    let differenceInDays = Math.round(differenceInMs / (1000 * 60 * 60 * 24));

    // Adjust for inclusive counting if needed
    if (inclusive && differenceInDays >= 0) {
        differenceInDays += 1;
    } else if (inclusive && differenceInDays < 0) {
        differenceInDays -= 1;
    }

    return differenceInDays;
}

export function convertDateToIso(date: string): string {
    let parsedDate;
    if (date?.includes('/')) {
        const parts = date.split('/');
        if (parts.length === 3) {
            const month = parts[0];
            const day = parts[1];
            const year = parts[2];

            parsedDate = year + '-' + month + '-' + day;
        }
    }
    return parsedDate ? parsedDate + getDSTHours(parsedDate + 'T00:00:00') : '';
}

export const tzOffset = (date: Date | string): string => {
    const offset =
        new Intl.DateTimeFormat('en', {
            timeZoneName: 'longOffset',
        })
        .formatToParts(new Date(date))
        .find((part) => part.type === 'timeZoneName')?.value || '';
    return offset.replace('GMT', '');
}

/**
* Calculates the Daylight Saving Time (DST) hours for a given time period or date *
* @returns {string} The number of hours offset for DST
*
* @example
* // Get DST hours for current date
* const dstOffset = getDSTHours();
* returns -04:00 if in DST
* returns -05:00 if not in DST
*/
export function getDSTHours(parsedDate: string): string {
    const date = new Date(parsedDate);
    const estTimeString = date.toLocaleString('en-US', {
        timeZone: DEFAULT_TIMEZONE,
        timeZoneName: 'short',
    });
    // Check if we're in DST by looking for 'EDT' in the timezone name
    const isDST = estTimeString.includes('EDT');
// EST is UTC-5, EDT is UTC-4
return isDST ? '-04:00' : '-05:00';
}
